import {
  Component,
  EventEmitter,
  Host,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { EoBaseFormControlComponent } from '../eo-base-form-control/eo-base-form-control.component';
import { IEoAutoCompleteConfig } from './eo-autocomplete.model';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { EoDropdownService } from '../eo-dropdown/eo-dropdown.service';
@Component({
  selector: 'eo-autocomplete',
  templateUrl: './eo-autocomplete.component.html',
  styleUrl: './eo-autocomplete.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EoAutoCompleteComponent),
      multi: true,
    },
    EoDropdownService,
  ],
})
export class EoAutoCompleteComponent<T> extends EoBaseFormControlComponent {
  protected override _config?: IEoAutoCompleteConfig<T>;
  override get config() {
    return this._config;
  }
  override set config(val: IEoAutoCompleteConfig<T> | undefined) {
    this._config = val;
  }

  httpData$ = this.dataService?.data$;
  httpStatus$ = this.dataService?.status$;
  httpError$ = this.dataService?.error$;

  @Input() autocompleteIcon?: string;
  @Input() inputStyleClass?: string;
  @Input() optionLabel?: string;
  @Input() optionValue?: string;
  @Input() suggestions?: T[];
  @Input() autocompleteStyleClass?: string;
  @Input() placeholder?: string;
  @Input() loadingPlaceholder?: string;
  @Input() showClear: boolean | null = null;
  @Input() showEmptyMessage: boolean | null = null;
  @Input() emptyMessage?: string;
  @Input() appendTo?: any;
  @Input() override loading = false;
  @Input() minLength?: number;
  @Input() maxLength?: number;
  @Input() multiple?: boolean;
  @Input() apiUrl?: string;

  @Output() select: EventEmitter<any> = new EventEmitter();
  @Output() search = new EventEmitter<any>();

  constructor(@Host() private readonly dataService?: EoDropdownService<T>) {
    super();
  }

  override ngOnInit(): void {
    this.fetchData();
  }

  fetchData(): void {
    const apiUrl = this.getApiUrl();
    if (!apiUrl) return;

    this.dataService?.getData(apiUrl).subscribe();
  }

  protected hasInputIcon(): boolean {
    return this.autocompleteIcon != null || this.config?.inputIcon != null;
  }
  protected getAutoCompleteIcon(): string {
    if (this.loading) return 'pi pi-spinner pi-spin';
    return (
      this.autocompleteIcon ?? this.config?.inputIcon ?? 'pi pi-chevron-down'
    );
  }
  protected getPlaceholder(): string {
    if (this.loading)
      return this.loadingPlaceholder ?? this.config?.loadingPlaceholder ?? '';

    return this.placeholder ?? this.config?.placeholder ?? '';
  }
  protected getInputStyleClass(): string {
    return this.inputStyleClass ?? this.config?.inputStyleClass ?? '';
  }
  protected getOptionLabel(): string {
    return this.optionLabel ?? this.config?.optionLabel ?? '';
  }
  protected getOptionValue(): string {
    return this.optionValue ?? this.config?.optionValue ?? '';
  }
  protected getSuggestions(): T[] {
    return (
      this.suggestions ??
      this.config?.suggestions ??
      this.httpData$?.getValue() ??
      []
    );
  }
  protected getAutoCompleteStyleClass(): string {
    return (
      this.autocompleteStyleClass ?? this.config?.autocompleteStyleClass ?? ''
    );
  }
  protected getLoadingPlaceholder(): string {
    return this.loadingPlaceholder ?? this.config?.loadingPlaceholder ?? '';
  }
  protected getShowClear(): boolean {
    return this.showClear ?? this.config?.showClear ?? false;
  }

  protected getAppendTo(): any {
    return this.appendTo ?? this.config?.appendTo ?? '';
  }

  protected filterSuggestions($event: AutoCompleteCompleteEvent) {
    this.search.emit($event.query);
  }
  protected getEmptyMessage(): string {
    return this.emptyMessage ?? this.config?.emptyMessage ?? '';
  }
  protected getShowEmptyMessage(): boolean {
    return this.showEmptyMessage ?? this.config?.showEmptyMessage ?? false;
  }

  protected getMinLength(): number | null {
    return this.minLength ?? this.config?.minLength ?? null;
  }

  protected getMaxLength(): number | null {
    return this.maxLength ?? this.config?.maxLength ?? null;
  }

  protected getMultiple(): boolean {
    return this.multiple ?? this.config?.multiple ?? false;
  }

  protected getApiUrl(): string | undefined {
    return this.apiUrl ?? this.config?.apiUrl;
  }
}
